import { useEffect, useRef, useCallback } from 'react';

const IDLE_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const;

interface UseIdleTimerOptions {
  idleMs: number;        // total idle time before logout (e.g. 30 * 60 * 1000)
  warningMs: number;     // how early to warn before logout (e.g. 60 * 1000)
  onWarn: () => void;    // called when warning threshold is crossed
  onLogout: () => void;  // called when idle time is exhausted
  enabled: boolean;      // only run when user is authenticated
}

export function useIdleTimer({ idleMs, warningMs, onWarn, onLogout, enabled }: UseIdleTimerOptions) {
  const logoutTimerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warnedRef       = useRef(false);

  const clearTimers = useCallback(() => {
    if (logoutTimerRef.current)  clearTimeout(logoutTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled) return;
    clearTimers();
    warnedRef.current = false;

    warningTimerRef.current = setTimeout(() => {
      warnedRef.current = true;
      onWarn();
    }, idleMs - warningMs);

    logoutTimerRef.current = setTimeout(() => {
      onLogout();
    }, idleMs);
  }, [enabled, idleMs, warningMs, onWarn, onLogout, clearTimers]);

  // Reset timer on any user activity
  useEffect(() => {
    if (!enabled) return;

    resetTimer();

    const handleActivity = () => {
      if (warnedRef.current) return; // warning is showing — don't auto-reset (user must click button)
      resetTimer();
    };

    IDLE_EVENTS.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));

    return () => {
      clearTimers();
      IDLE_EVENTS.forEach((evt) => window.removeEventListener(evt, handleActivity));
    };
  }, [enabled, resetTimer, clearTimers]);

  return { resetTimer };
}
